import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  useDealerPortal,
  DEFAULT_VEHICLES_PAGE_SIZE,
  VEHICLES_PAGE_SIZES,
} from "@/hooks/useDealerPortal";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels } from "@/i18n";
import { DealerPortalCreateVehicleDialog } from "./DealerPortalCreateVehicleDialog";
import type {
  DealerPortalVehicle,
  DealerPortalVehicleStatus,
} from "@/types/dealerPortal";
import { VEHICLE_STATUSES } from "@/types/dealerPortal";
import { getDealerPortalVehicleStatusLabels } from "@/i18n";
import {
  Car,
  Search,
  ChevronLeft,
  ChevronRight,
  FileImage,
  PenLine,
} from "lucide-react";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { Badge } from "@/components/ui/badge";
import { NativeImage } from "@/components/native/NativeImage";
import { useMedia } from "@/hooks/useMedia";
import { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_BG: Record<DealerPortalVehicleStatus | "all", string> = {
  all: "bg-muted text-muted-foreground hover:bg-muted/80",
  TRADE_IN:
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300 hover:opacity-90",
  INBOUND:
    "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 hover:opacity-90",
  PICKED_UP:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 hover:opacity-90",
  DELIVERED:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 hover:opacity-90",
  SOLD: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 hover:opacity-90",
  TEST: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 hover:opacity-90",
  IN_STOCK:
    "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 hover:opacity-90",
};

const BADGE_VARIANT: Record<DealerPortalVehicleStatus, string> = {
  TRADE_IN:
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300 border-0",
  INBOUND:
    "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 border-0",
  PICKED_UP:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border-0",
  DELIVERED:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border-0",
  SOLD: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 border-0",
  TEST: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border-0",
  IN_STOCK:
    "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 border-0",
};

function VehicleThumb({
  vehicle,
  className,
}: {
  vehicle: DealerPortalVehicle;
  className?: string;
}) {
  const { getDownloadUrlAsync } = useMedia();
  const [src, setSrc] = useState<string | null>(null);
  const documents = vehicle.documents ?? [];
  // Use same thumbnail as vehicle card Bilder > Exterior > Thumbnail: first EXTERIOR image doc's PREVIEW
  const imageDocs = documents.filter((d) => {
    const arr = Array.isArray(d.media) ? d.media : d.media ? [d.media] : [];
    return arr.some((m: { mimeType?: string }) => m.mimeType?.startsWith("image/"));
  });
  const exteriorDocs = imageDocs.filter(
    (d) => (d.imageCategory ?? "").toUpperCase() === "EXTERIOR",
  );
  const thumbnailDoc = exteriorDocs[0] ?? imageDocs[0];
  const firstImageMedia = thumbnailDoc
    ? (() => {
        const arr = Array.isArray(thumbnailDoc.media)
          ? thumbnailDoc.media
          : thumbnailDoc.media
            ? [thumbnailDoc.media]
            : [];
        const preview = arr.find(
          (m: { mediaSize?: string | null; mimeType?: string }) =>
            String(m.mediaSize ?? "").toUpperCase() === "PREVIEW" && m.mimeType?.startsWith("image/"),
        );
        return preview ?? arr.find((m: { mimeType?: string }) => m.mimeType?.startsWith("image/"));
      })()
    : undefined;
  const mediaId = firstImageMedia?.id;

  useEffect(() => {
    if (!mediaId) return;
    let cancelled = false;
    getDownloadUrlAsync({ mediaId, expiresIn: 3600, preferFullSize: false })
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mediaId, getDownloadUrlAsync]);

  if (!mediaId || !src) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg bg-muted ${className ?? ""}`}
      >
        <Car className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
      </div>
    );
  }
  return (
    <NativeImage
      src={src}
      alt=""
      className={`rounded-lg object-cover bg-muted ${className ?? ""}`}
    />
  );
}

export function DealerPortalVehiclesTab() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const d = dealerPortalLabels(language);
  const v = d.vehicles as Record<string, string>;
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<
    Set<DealerPortalVehicleStatus>
  >(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedPageSize(
    "pageSize:dealerPortalVehicles",
    DEFAULT_VEHICLES_PAGE_SIZE,
    [...VEHICLES_PAGE_SIZES],
  );

  const statusParam =
    selectedStatuses.size > 0
      ? Array.from(selectedStatuses).join(",")
      : undefined;
  const { vehiclesPaginated } = useDealerPortal({
    vehiclesSearch: appliedSearch ?? undefined,
    vehiclesStatus: statusParam,
    vehiclesPage: page,
    vehiclesPageSize: pageSize,
  });

  const paginated = vehiclesPaginated.data;
  const items = paginated?.items ?? [];
  const total = paginated?.total ?? 0;
  const totalPages =
    pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const isLoading = vehiclesPaginated.isLoading;

  const toggleStatus = (status: DealerPortalVehicleStatus) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };
  const selectAll = () => setSelectedStatuses(new Set());

  useEffect(() => {
    setPage(1);
  }, [selectedStatuses, appliedSearch, pageSize]);

  return (
    <div className="space-y-4">
      {/* Desktop: en rad med titel + sök + Sök + Lägg till + statusknappar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-2 lg:gap-3">
          <h2 className="text-lg font-semibold shrink-0">
            {v.title ?? "Fordon"}
          </h2>
          <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
            <Input
              placeholder={v.searchPlaceholder ?? "Reg.nr, märke, modell..."}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" &&
                setAppliedSearch(searchInput.trim() || null)
              }
              className="w-full sm:w-[200px]"
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setAppliedSearch(searchInput.trim() || null)}
              className="gap-1 shrink-0"
              aria-label={v.search ?? "Sök"}
            >
              <Search className="h-3.5 w-3.5" />
              {v.search ?? "Sök"}
            </Button>
            <Button
              size="sm"
              className="shrink-0"
              onClick={() => setChoiceDialogOpen(true)}
            >
              {v.addVehicle ?? "Lägg till fordon"}
            </Button>
          </div>
        </div>

        {/* Statusfilter – flera valbara; samma rad på desktop */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={selectAll}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity ${STATUS_BG.all} ${selectedStatuses.size === 0 ? "ring-2 ring-primary" : ""}`}
          >
            {v.statusAll ?? "Alla"}
          </button>
          {VEHICLE_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => toggleStatus(status)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity ${STATUS_BG[status]} ${selectedStatuses.has(status) ? "ring-2 ring-primary" : ""}`}
            >
              {getDealerPortalVehicleStatusLabels(language)[status]}
            </button>
          ))}
        </div>
      </div>

      {/* List: 1 per row */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground py-6">
          {v.loading ?? "Laddar..."}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6">
          {appliedSearch || selectedStatuses.size > 0
            ? (v.noMatches ?? "Inga fordon matchar filter.")
            : (v.empty ?? "Inga fordon än.")}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {items.map((vehicle) => (
              <button
                key={vehicle.id}
                type="button"
                onClick={() =>
                  navigate(`/dealer-portal/vehicles/${vehicle.id}`)
                }
                className="flex items-center gap-3 rounded-xl border bg-card p-3 text-left w-full hover:shadow-md transition-shadow"
              >
                <VehicleThumb
                  vehicle={vehicle}
                  className="h-14 w-14 sm:h-16 sm:w-16 shrink-0 aspect-square"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <LicensePlate lpn={vehicle.regNr} className="text-xs" />
                    {vehicle.status && (
                      <Badge
                        className={`text-xs font-normal ${BADGE_VARIANT[vehicle.status] ?? ""}`}
                      >
                        {getDealerPortalVehicleStatusLabels(language)[
                          vehicle.status
                        ] ?? vehicle.status}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground truncate">
                    {vehicle.make} {vehicle.model} ({vehicle.year})
                    {vehicle.parkingSpot ? ` · ${vehicle.parkingSpot}` : ""}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination – visa alltid när det finns data (så att sidstorlek 10|25|50 och total syns) */}
          {totalPages > 1 || total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {v.pageSize ?? "Per sida"}:
                </span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(val) => {
                    setPageSize(Number(val));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLES_PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  {total} {v.total ?? "totalt"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  {v.page ?? "Sida"} {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Choice: Post ad flow vs manual vehicle card */}
      <Dialog open={choiceDialogOpen} onOpenChange={setChoiceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {v.addVehicleChoiceTitle ?? v.addVehicle ?? "Lägg till fordon"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {v.addVehicleChoiceDesc ?? "Välj hur du vill lägga till fordon:"}
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => {
                setChoiceDialogOpen(false);
                navigate("/car-post-an-ad");
              }}
            >
              <FileImage className="h-5 w-5 shrink-0" />
              <span>
                {v.addVehicleOptionPostAd ??
                  "Lägg upp annons / Skapa fordonstest"}
              </span>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => {
                setChoiceDialogOpen(false);
                setCreateOpen(true);
              }}
            >
              <PenLine className="h-5 w-5 shrink-0" />
              <span>
                {v.addVehicleOptionManual ?? "Lägg upp manuellt fordonskort"}
              </span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DealerPortalCreateVehicleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
